<template>
    <div class="c-product-variation-line" :class="{'isDisabled' : isDisabled, 'is-campaign-border': isCampaign }" v-if="hasPrice" :id="'variant_' + product.Id">
        <v-loading :data="loaded"></v-loading>
        <div v-show="isCampaign" class="is-campaign">
            <div class="campaign__text">{{ $t('product.thumb.preOrder_campagneModel') }}</div>
        </div>
        <div class="u-flex-set o-gutter-large">
            <div class="c-product-color u-flex--push-right">
                <img v-if="variationImage" :src="variationImage" alt="" class="c-product-variation-image">
                <div v-else class="c-product-color-element" :style="`background-color: ${product.HexCode}`"></div>
                <p class="c-product-color-info">
                    <strong>{{ $t('product.line.color') }}: {{ product.ColorId }} {{ product.Color }}</strong> <br>
                </p>
            </div>
            <div>
                <template v-if="deliveryDateOptions.length">
                    <v-select v-model="product.DeliveryDate"
                              :selected="product.DeliveryDate"
                              name="color_delivery"
                              size="tiny"
                              :disabled="readOnly"
                              :disable-expanded="true"
                              :options="deliveryDateOptions"
                              :fluid="true">
                        <strong class="u-font-default" slot="label">{{ $t('product.thumb.delivery') }}:</strong>
                    </v-select>
                    <br>
                    <span class="deadline-date">{{ $t('product.thumb.final_orderdate') }}: <strong>{{ finalOrderDate }}</strong></span>
                    <br>
                </template>
            </div>
            <div v-if="!isPersoneel && !isMarketing && !isCustomerHidden">
                <strong>{{ $t('product.line.retail_price') }}: {{ AppliedRetailFromPrice | currency(customerCurrencySymbol) }}</strong>
            </div>
            <div class="u-flex--no-grow">
                <div class="c-product-variation-line__last">
                    <strong class="u-block u-margin-bottom-tiny">{{ $t('product.line.from_price') }}: {{ AppliedOriginalFromPrice | currency(customerCurrencySymbol) }}</strong>
                    <v-input v-if="isEditableField"
                             v-model.number="product.PreOrderDiscount"
                             type="number" :min=0 :max="MaximumDiscount" :name="`${_uid}-discount`" input-size="tiny"
                             width="50px"
                             :disabled="isDisabled || readOnly">
                        <slot slot="label"><span class="u-font-small">{{ $t('product.line.discount') }}:</span></slot>
                        <slot slot="suffix"><span class="u-font-small u-text-nowrap">(max. {{ product.MaximumDiscount }}%)</span></slot>
                    </v-input>
                </div>
            </div>
        </div>

        <div class="c-product-seperator">
        </div>

        <div class="u-margin-bottom-tiny">
            <button class="c-button c-button--alpha c-button--small" type="button"
                    @click="copy" :disabled="sizesDisabled">
                <v-copy-icon class="svg-icon svg-fill"></v-copy-icon>
                {{ $t('global.copy_button') }}
            </button>
            <button class="c-button c-button--alpha c-button--small" type="button"
                    @click="paste" :disabled="pasteDisabled">
                <v-paste-icon class="svg-icon svg-fill"></v-paste-icon>
                {{ $t('global.paste_button') }}
            </button>
        </div>

        <div class="c-product-variation-line-sizes u-flexed-item--auto u-margin-bottom-tiny">
            <template v-if="isSizePants">
                <v-product-pants-sizes :readOnly="sizesDisabled" :items="product.Variants" :waist-sizes="product.VariantsDim1" :length-sizes="product.VariantsDim2" ref="productPantsSizes" :showStockForPreOrder="showStockForPreOrder"></v-product-pants-sizes>
            </template>
            <template v-else>
                <v-product-size-input v-for="item in productSizes" :disabled="sizesDisabled" :key="item.Id" :item.sync="item" @loaded="toggleLoaded" ref="productSizeInputs" :showStockForPreOrder="showStockForPreOrder"></v-product-size-input>
            </template>
        </div>
        <div>
            <div class="u-flex-set u-flex--wrap o-spacing">
                <div>
                    <strong>{{ $t('product.line.total_amount') }}:</strong>
                    <span>{{ TotalAmount }}</span>
                </div>
                <div>
                    <strong>{{ $t('product.line.total_price') }}:</strong>
                    <span>{{ TotalPrice | currency(customerCurrencySymbol)}}</span>
                </div>
                <div v-if="!isEditableField && product.PreOrderDiscount !== 0">
                    <strong>{{ $t('product.line.discount') }}:</strong>
                    <span>{{ product.PreOrderDiscount }}%</span>
                </div>

                <div>
                    <strong>{{ $t('product.line.netto_price') }}:</strong>
                    <span>{{ NettoPrice | currency(customerCurrencySymbol) }}</span>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
    import Vue from 'vue'
    import VLoading from '@/components/Loading'
    import debounce from 'lodash/debounce'
    import VSelect from '@/components/form/Select'
    import VInput from '@/components/form/Input'
    import VProductSizeInput from '@/components/product/widgets/productSizeInput'
    import VProductPantsSizes from '@/components/product/widgets/productPantsSizes'
    import VCopyIcon from '@/assets/images/svg/copy.svg'
    import VPasteIcon from '@/assets/images/svg/paste.svg'

    export default {
        components: {
            VLoading,
            VSelect,
            VInput,
            VProductSizeInput,
            VProductPantsSizes,
            VCopyIcon,
            VPasteIcon
        },
        props: {
            product: Object,
            parentId: String,
            groupIndex: Number,
            isEditableField: {type: Boolean},
            readOnly: {type: Boolean, default: false},
            sizeChartCopy: Object
        },
        data () {
            return {
                loaded: { isLoaded: true },
                productSizes: [].concat.apply([], this.product.Variants),
                isSizePants: this.product.SizeChartId === 2
            }
        },
        watch: {
            "product.PreOrderDiscount" () {
                this.updateProduct();
            },
            "product.DeliveryDate" () {
                this.updateProduct();
            }
        },
        computed: {
            /**
             * @return {number}
             */
            TotalAmount() {
                let total = 0;

                if (this.productSizes && this.productSizes.length > 0) {
                    this.productSizes.forEach(size => {
                        if (size) { total += size.Amount }
                    });
                }

                // Set amount for parent usage
                this.product.TotalAmount = total;

                return total;
            },
            /**
             * @return {number}
             */
            TotalPrice() {
              let totalPrice = this.TotalAmount * this.AppliedRetailFromPrice
              this.product.TotalPrice = totalPrice;
              return totalPrice;
            },
            /**
             * @return {number}
             */
            NettoPrice() {
              let nettoPrice = this.TotalPrice * ((100 - this.product.PreOrderDiscount) / 100);
              this.product.NettoPrice = nettoPrice;
              return nettoPrice;
            },

            variationImage () {
                let output = null;
                if (this.product.Images.length) {
                    output = this.product.Images[0].replace('[WIDTH]', 60).replace('[HEIGHT]', 60);
                }
                return output;
            },
            deliveryDateOptions () {
                let output = [];
                this.product.DeliveryDates.forEach((month) => {
                    output.push({
                        value: month.DeliveryDate,
                        content: Vue.moment(month.DeliveryDate).format('DD-MM-YYYY'),
                        selected: this.product.DeliveryDate === month.DeliveryDate
                    });
                });
                return output;
            },
            finalOrderDate () {
                let finalDateStr = this.filterFinalDate();
                let finalOrderDateObj = Vue.moment(finalDateStr[0].FinalOrderDate);

                if (finalOrderDateObj > Vue.moment('1970-01-01T00:00:00')) {
                    return finalOrderDateObj.format('DD-MM-YYYY');
                } else {
                    return this.$t('product.thumb.deadline_deleted');
                }
            },
            AppliedRetailFromPrice () {
                let priceStr = this.filterFinalDate();
                let appliedRetailFromPrice = 0;
                
                if(typeof priceStr[0] !== 'undefined') {
                    appliedRetailFromPrice = priceStr[0].RetailFromPrice;
                }

                //RAB2B-925: use normal RetailFromPrice as fallback
                return (appliedRetailFromPrice > 0) ? appliedRetailFromPrice : this.product.RetailFromPrice;
            },
            AppliedOriginalFromPrice () {
                let priceStr = this.filterFinalDate();
                let appliedOriginalFromPrice = 0;
                
                if(typeof priceStr[0] !== 'undefined') {
                    appliedOriginalFromPrice = priceStr[0].OriginalFromPrice;
                }

                //RAB2B-925: use normal OriginalFromPrice as fallback
                return (appliedOriginalFromPrice > 0) ? appliedOriginalFromPrice : this.product.OriginalFromPrice;
            },
            MaximumDiscount () {
                let discount;
                if (this.product.MaximumDiscount) {
                    discount = this.product.MaximumDiscount;
                } else {
                    discount = 0;
                }
                return discount;
            },
            isDisabled () {
                return this.product.IsDeleted;
            },
            isCampaign() {
                return this.product.IsCampaignModel;
            },
            isPersoneel() {
                return this.$store.getters.isPersoneel;
            },
            isMarketing() {
                return this.$store.getters.isMarketing;
            },
            isCustomerHidden() {
                return this.$store.getters.isCustomerHidden;
            },
            sizesDisabled() {
                return this.deliveryDateOptions.length == 0 || this.readOnly;
            },
            customerCurrencySymbol() { 
                return this.$store.getters.userInfo.customerCurrencySymbol;
            },
            hasPrice(){
                return this.product.RetailFromPrice >= 0 && this.product.OriginalFromPrice >= 0;
            },
            pasteDisabled() {
                let somethingCopied = this.sizeChartCopy && this.sizeChartCopy.SizeChartId === this.product.SizeChartId;
                return this.sizesDisabled || !somethingCopied;
            },
            showStockForPreOrder() {
                if(this.$store.getters.isAdmin === true || this.$store.getters.isSalesman === true) {
                    let finalDateStr = this.filterFinalDate();

                    if(finalDateStr.length > 0) {
                        let finalOrderDateObj = Vue.moment(finalDateStr[0].FinalOrderDate);
                        let today = Vue.moment();
                        
                        if(today > finalOrderDateObj) {
                            return true;
                        }
                    }
                }

                return false;
            }
        },
        methods: {
            updateProduct: debounce(function () {
                if (this.product.PreOrderDiscount <= this.product.MaximumDiscount) {
                    this.toggleLoaded({});
                    this.$store.dispatch('updatePreOrderProduct', {
                        preOrderId: this.$route.params.id,
                        productId: this.product.Id,
                        body: {
                            Id: this.product.Id,
                            PreOrderDiscount: this.product.PreOrderDiscount,
                            DeliveryDate: this.product.DeliveryDate
                        }
                    }).then(() => {
                        this.$store.dispatch('getPreOrderTotalsById', this.$route.params.id);
                        this.toggleLoaded({isLoaded: true});
                    }).catch(() => {
                        this.loaded = {isLoaded: true};
                    });
                }
            }, 500),
            filterFinalDate() {
                return this.product.DeliveryDates.filter((date) => {
                    return date.DeliveryDate === this.product.DeliveryDate;
                })
            },
            toggleLoaded(value) {
                this.loaded = value;
            },
            copy() {
                let sizeChartCopy = {
                    SizeChartId: this.product.SizeChartId,
                    Variants: []
                };
                
                this.product.Variants.forEach(item => {
                    item.forEach(variant => {
                        if(variant !== null && variant.Amount !== null) {
                            sizeChartCopy.Variants.push({
                                Amount: variant.Amount,
                                Size: variant.Size
                            });
                        }
                    });
                });

                this.$emit('sizeChartCopied', sizeChartCopy)
            },
            paste() {
                this.product.Variants.forEach(item => {
                    item.forEach(variant => {
                        if(variant !== null) {
                            let variantCopy = this.sizeChartCopy.Variants.find(copy => {
                                return copy.Size === variant.Size;
                            });

                            if(variant.IsEnabled === true && variant.IsAvailable === true){
                                if(variantCopy) {
                                    variant.Amount = variantCopy.Amount;
                                } else {
                                    variant.Amount = null;
                                }
                            }
                        }
                    });
                });

                if(this.isSizePants){
                    this.$refs.productPantsSizes.$refs.sizeInputs.forEach(item => item.updateItem());
                } else {
                    this.$refs.productSizeInputs.forEach(item => item.updateItem());
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import "~@/assets/scss/private";

    .deadline-date {
        font-size: 14px;
        font-style: italic;
    }

    .c-product-variation-line {
        &.isDisabled {
            opacity: .5;
        }
    }

    .c-product-variation-line-sizes {
        flex-wrap: wrap;
    }
</style>

<style lang="scss">
    .c-product-group .is-loading {
        bottom: 0;
    }
</style>



// WEBPACK FOOTER //
// src/components/product/common/ProductVariationLine.vue